// src/authpages/group/GroupSession.js
import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, 
  UserCheck, 
  MessageSquare, 
  Video, 
  Clock, 
  X, 
  Send, 
  ChevronLeft,
  Play,
  Pause,
  RefreshCw,
  Volume2,
  VolumeX,
  Trophy
} from 'lucide-react';
import { auth, db } from '../../firebase';
import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  onSnapshot, 
  serverTimestamp,
  doc,
  getDoc,
  updateDoc
} from 'firebase/firestore';
import { recordStudySession } from '../dashboard/StudyStatsService';
import VideoCall from './VideoCall';

const GroupSession = ({ group, currentUserId, getFriendNameById, endSession }) => {
  // Chat state
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [activeTab, setActiveTab] = useState('chat'); // chat, video, timer
  
  // Timer state
  const [timerActive, setTimerActive] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(group.timerSettings.workTime * 60); // in seconds
  const [timerMode, setTimerMode] = useState('work'); // work, break, longBreak
  const [sessions, setSessions] = useState(0);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [recordingSuccess, setRecordingSuccess] = useState(null); // Track recording success/failure
  
  // Refs
  const chatContainerRef = useRef(null);
  const timerRef = useRef(null);
  const alarmSound = useRef(null);
  
  // Log Firebase auth state for debugging
  console.log('Firebase auth state:', auth.currentUser ? 'Authenticated' : 'Not authenticated');
  
  // Replace the useEffect for loading messages with this improved version
  useEffect(() => {
    const chatRoomId = group.chatRoomId;
    console.log('Loading messages for chatRoomId:', chatRoomId);
    
    // Create a query to get messages for this chat room
    const messagesRef = collection(db, 'studyGroupMessages');
    const q = query(
      messagesRef,
      where('chatRoomId', '==', chatRoomId),
      orderBy('timestamp', 'asc')
    );
    
    // Subscribe to real-time updates
    const unsubscribe = onSnapshot(q, 
      (snapshot) => {
        const loadedMessages = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Loaded messages:', loadedMessages.length);
        setMessages(loadedMessages);
        
        // Scroll to bottom on new messages
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      },
      (error) => {
        console.error("Error loading messages:", error);
      }
    );
    
    // Update group's lastActive timestamp - wrap in try/catch to prevent errors
    try {
      const groupRef = doc(db, 'studyGroups', group.id);
      updateDoc(groupRef, { lastActive: serverTimestamp() })
        .catch(error => console.error("Error updating lastActive timestamp:", error));
    } catch (error) {
      console.error("Error updating group timestamp:", error);
    }
    
    return () => {
      console.log('Unsubscribing from messages listener');
      unsubscribe();
    };
  }, [group.chatRoomId, group.id]);

  // Function to record completed study session - uses your existing recordStudySession
  const recordCompletedSession = async (sessionMinutes) => {
    try {
      console.log(`Recording completed study session: ${sessionMinutes} minutes`);
      setRecordingSuccess(null); // Reset recording status
      
      // Format session data for your existing recordStudySession function
      const sessionData = {
        minutes: sessionMinutes,
        sessionType: 'focus',
        hadBreaks: false,
        quizCompleted: false,
        studyPartners: group.members.filter(id => id !== currentUserId),
        topicId: group.id,
        notes: `Study session in group: ${group.name}`
      };
      
      // Use your existing recordStudySession function with a small delay
      // to avoid state update conflicts
      await new Promise(resolve => setTimeout(resolve, 100));
      const success = await recordStudySession(sessionData);
      
      console.log("Session recording result:", success);
      setRecordingSuccess(success); // Update status to show feedback to user
      
      // Set localStorage flags for dashboard to detect
      localStorage.setItem('studySessionCompleted', 'true');
      localStorage.setItem('studySessionCompletedAt', new Date().toISOString());
      
      return success;
    } catch (error) {
      console.error("Error recording study session:", error);
      setRecordingSuccess(false); // Show failure to user
      
      // Set localStorage flags even if there was an error
      localStorage.setItem('studySessionCompleted', 'true');
      localStorage.setItem('studySessionCompletedAt', new Date().toISOString());
      
      return false;
    }
  };
  
  // Timer effect with proper cleanup and race condition prevention
  useEffect(() => {
    let isMounted = true;
    let recordingInProgress = false;
    
    if (timerActive) {
      timerRef.current = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            // Timer finished
            clearInterval(timerRef.current);
            
            // Play alarm if sound is enabled
            if (soundEnabled && alarmSound.current) {
              alarmSound.current.play();
            }
            
            // Handle mode switch and session recording
            if (timerMode === 'work' && isMounted && !recordingInProgress) {
              const completedSessions = sessions + 1;
              
              // Set recording flag to prevent race conditions
              recordingInProgress = true;
              
              // Record the completed work session
              // Use a setTimeout to avoid state update conflicts
              setTimeout(() => {
                recordCompletedSession(group.timerSettings.workTime)
                  .finally(() => {
                    recordingInProgress = false;
                  });
              }, 100);
              
              if (isMounted) {
                setSessions(completedSessions);
              }
              
              if (completedSessions % group.timerSettings.longBreakInterval === 0 && 
                  group.timerSettings.longBreakTime) {
                // Time for a long break
                if (isMounted) {
                  setTimerMode('longBreak');
                }
                return group.timerSettings.longBreakTime * 60;
              } else {
                // Regular break
                if (isMounted) {
                  setTimerMode('break');
                }
                return group.timerSettings.breakTime * 60;
              }
            } else {
              // Back to work (after break)
              if (isMounted) {
                setTimerMode('work');
              }
              return group.timerSettings.workTime * 60;
            }
          }
          return prev - 1;
        });
      }, 1000);
    } else if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    return () => {
      isMounted = false;
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [timerActive, timerMode, sessions, group.timerSettings, soundEnabled, group.members, group.name, group.id, currentUserId]);
  
  // Format time for display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Improved sendMessage function with optimistic UI updates
const sendMessage = async (e) => {
  e.preventDefault();
  if (!newMessage.trim()) return;
  
  // Create the message object
  const messageData = {
    chatRoomId: group.chatRoomId,
    text: newMessage,
    senderId: currentUserId,
    senderName: auth.currentUser?.displayName || 'You',
    timestamp: new Date() // Use client-side date for immediate display
  };
  
  // Create a temporary ID for the optimistic update
  const tempId = `temp-${Date.now()}`;
  
  // Optimistically update the UI immediately
  setMessages(prevMessages => [
    ...prevMessages, 
    { id: tempId, ...messageData }
  ]);
  
  // Scroll to bottom for immediate feedback
  if (chatContainerRef.current) {
    chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
  }
  
  // Clear the message input
  setNewMessage('');
  
  try {
    // Create a reference to the messages collection
    const messagesCollection = collection(db, 'studyGroupMessages');
    
    // Create the message document in Firebase
    await addDoc(messagesCollection, {
      ...messageData,
      timestamp: serverTimestamp() // Replace with server timestamp for database
    });
    
    console.log('Message sent successfully to Firebase');
  } catch (error) {
    console.error('Error sending message:', error);
    
    // On error, remove the temporary message from the UI
    setMessages(prevMessages => 
      prevMessages.filter(message => message.id !== tempId)
    );
    
    alert(`Failed to send message: ${error.message}. Please try again.`);
  }
};
  
  return (
    <div className="bg-gray-50 min-h-screen flex flex-col">
      {/* Header */}
      <div className="bg-blue-600 text-white p-4 shadow-md">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button 
              onClick={endSession}
              className="p-2 rounded-full hover:bg-blue-500 transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <h1 className="text-xl font-bold">{group.name}</h1>
            <div className="bg-blue-500 px-3 py-1 rounded-full text-xs">
              {group.subject}
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex -space-x-2">
              {group.members.slice(0, 3).map((memberId, idx) => (
                <div key={idx} className="w-8 h-8 rounded-full bg-blue-300 flex items-center justify-center border-2 border-blue-600">
                  <span className="text-xs font-bold">
                    {(memberId === currentUserId ? 'You' : getFriendNameById(memberId)).charAt(0)}
                  </span>
                </div>
              ))}
              {group.members.length > 3 && (
                <div className="w-8 h-8 rounded-full bg-blue-400 flex items-center justify-center border-2 border-blue-600">
                  <span className="text-xs font-bold">+{group.members.length - 3}</span>
                </div>
              )}
            </div>
            <div className="text-sm">
              {group.memberCount} members
            </div>
          </div>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="bg-white shadow-sm px-4 py-2 rounded-b-lg">
        <div className="max-w-6xl mx-auto flex space-x-4">
          {[
            { key: 'chat', label: 'Group Chat', icon: MessageSquare },
            { key: 'video', label: 'Video Call', icon: Video },
            { key: 'timer', label: 'Study Timer', icon: Clock }
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              className={`py-2 px-4 flex items-center transition-colors ${
                activeTab === key 
                  ? 'text-blue-600 border-b-2 border-blue-600 font-medium' 
                  : 'text-gray-600 hover:text-blue-500'
              }`}
              onClick={() => setActiveTab(key)}
            >
              <Icon className="mr-2" size={18} /> {label}
            </button>
          ))}
        </div>
      </div>
      
      {/* Content area */}
      <div className="flex-1 p-4 overflow-hidden">
        <div className="max-w-6xl mx-auto h-full">
          
          {/* Chat Tab */}
          {activeTab === 'chat' && (
            <div className="bg-white rounded-lg shadow-md border h-full flex flex-col">
              <div className="p-3 border-b bg-gray-50 rounded-t-lg">
                <h2 className="font-medium flex items-center">
                  <MessageSquare className="mr-2 text-blue-500" size={18} />
                  Group Chat
                </h2>
              </div>
              
              <div 
                className="flex-1 p-4 overflow-y-auto"
                ref={chatContainerRef}
              >
                {messages.length === 0 ? (
                  <div className="text-center py-10 text-gray-500">
                    <MessageSquare className="mx-auto h-10 w-10 text-gray-300 mb-2" />
                    <p>No messages yet. Start the conversation!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((message) => (
                      <div 
                        key={message.id}
                        className={`flex ${message.senderId === currentUserId ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`max-w-xs md:max-w-md rounded-lg p-3 shadow-sm ${
                            message.senderId === currentUserId 
                              ? 'bg-blue-500 text-white rounded-br-none' 
                              : 'bg-gray-100 text-gray-800 rounded-bl-none'
                          }`}
                        >
                          {message.senderId !== currentUserId && (
                            <div className="font-medium text-xs mb-1">
                              {getFriendNameById(message.senderId)}
                            </div>
                          )}
                          <p>{message.text}</p>
                          <div className={`text-xs mt-1 ${message.senderId === currentUserId ? 'text-blue-100' : 'text-gray-500'}`}>
                            {message.timestamp ? 
                              (message.timestamp.toDate ? 
                                message.timestamp.toDate().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) :
                                message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                              ) : 'Just now'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <form 
                onSubmit={sendMessage}
                className="p-3 border-t flex items-center"
              >
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  className="flex-1 p-2 border rounded-l-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  className="bg-blue-500 text-white p-2 rounded-r-md hover:bg-blue-600 transition-colors"
                >
                  <Send size={20} />
                </button>
              </form>
            </div>
          )}

          {/* Video Call Tab */}
{activeTab === 'video' && (
  <div className="bg-white rounded-lg shadow-md border h-full flex flex-col">
    <div className="p-3 border-b bg-gray-50 rounded-t-lg">
      <h2 className="font-medium flex items-center">
        <Video className="mr-2 text-blue-500" size={18} />
        Video Call
      </h2>
    </div>
    
    <div className="flex-1 overflow-hidden">
      <VideoCall 
        group={group}
        currentUserId={currentUserId}
        getFriendNameById={getFriendNameById}
        onCallEnd={() => {
          // Optional: Handle call end event if needed
          console.log("Video call ended");
        }}
      />
    </div>
  </div>
)}
          
          {/* Timer Tab */}
          {activeTab === 'timer' && (
            <div className="bg-white rounded-lg shadow-md border h-full flex flex-col">
              <div className="p-3 border-b bg-gray-50 rounded-t-lg">
                <h2 className="font-medium flex items-center">
                  <Clock className="mr-2 text-blue-500" size={18} />
                  {group.timerMethod} Timer
                </h2>
              </div>
              
              <div className="flex-1 flex flex-col items-center justify-center p-6">
                <div className="text-center mb-8">
                  <div className="text-sm font-medium text-gray-500 mb-1">
                    {timerMode === 'work' ? 'FOCUS TIME' : timerMode === 'break' ? 'SHORT BREAK' : 'LONG BREAK'}
                  </div>
                  <div className="text-6xl font-bold mb-2 text-blue-600">
                    {formatTime(timeRemaining)}
                  </div>
                  <div className="text-sm text-gray-500">
                    {sessions} completed sessions
                  </div>
                  {sessions > 0 && (
                    <div className="text-xs text-green-600 mt-1">
                      Sessions are tracked for your stats & rewards
                    </div>
                  )}
                  {recordingSuccess === true && (
                    <div className="text-xs text-green-600 mt-1 bg-green-50 p-1 rounded-md">
                      ✓ Session successfully recorded
                    </div>
                  )}
                  {recordingSuccess === false && (
                    <div className="text-xs text-red-600 mt-1 bg-red-50 p-1 rounded-md">
                      ! Error recording session, but we saved your time locally
                    </div>
                  )}
                </div>
                
                <div className="flex space-x-4 mb-8">
                  <button
                    onClick={() => setTimerActive(!timerActive)}
                    className={`p-4 rounded-full transition-colors ${
                      timerActive ? 'bg-red-500 text-white' : 'bg-green-500 text-white'
                    }`}
                  >
                    {timerActive ? <Pause size={24} /> : <Play size={24} />}
                  </button>
                  
                  <button
                    onClick={() => {
                      setTimerActive(false);
                      // Reset based on current mode
                      if (timerMode === 'work') {
                        setTimeRemaining(group.timerSettings.workTime * 60);
                      } else if (timerMode === 'break') {
                        setTimeRemaining(group.timerSettings.breakTime * 60);
                      } else {
                        setTimeRemaining(group.timerSettings.longBreakTime * 60);
                      }
                    }}
                    className="p-4 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                  >
                    <RefreshCw size={24} />
                  </button>
                  
                  <button
                    onClick={() => setSoundEnabled(!soundEnabled)}
                    className="p-4 rounded-full bg-gray-200 text-gray-700 hover:bg-gray-300 transition-colors"
                  >
                    {soundEnabled ? <Volume2 size={24} /> : <VolumeX size={24} />}
                  </button>
                </div>
                
                <div className="space-y-4 max-w-md">
                  <div className="bg-blue-50 p-4 rounded-lg shadow-sm">
                    <h3 className="font-medium text-blue-800 mb-2">Timer Settings</h3>
                    <ul className="space-y-2 text-sm text-gray-700">
                      <li className="flex justify-between">
                        <span>Focus Time:</span>
                        <span className="font-medium">{group.timerSettings.workTime} minutes</span>
                      </li>
                      <li className="flex justify-between">
                        <span>Break Time:</span>
                        <span className="font-medium">{group.timerSettings.breakTime} minutes</span>
                      </li>
                      {group.timerSettings.longBreakTime && (
                        <>
                          <li className="flex justify-between">
                            <span>Long Break:</span>
                            <span className="font-medium">{group.timerSettings.longBreakTime} minutes</span>
                          </li>
                          <li className="flex justify-between">
                            <span>Long Break After:</span>
                            <span className="font-medium">{group.timerSettings.longBreakInterval} sessions</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                  
                  <div className="bg-green-50 p-4 rounded-lg shadow-sm border border-green-100">
                    <h3 className="font-medium text-green-800 mb-2 flex items-center">
                      <Trophy className="mr-2" size={16} />
                      Stats & Rewards
                    </h3>
                    <p className="text-sm text-green-700">
                      Completed focus sessions are automatically tracked for your dashboard statistics and count toward earning badges and XP.
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Hidden audio element for timer sound */}
              <audio 
                ref={alarmSound}
                src="/timer-sound.mp3" // You'll need to add this file to your public directory
                preload="auto"
              />
            </div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default GroupSession;